import { useEffect, useState } from "react";

import { NATIVE_BY_PATH, nativeLandingFor } from "@/lib/native/aiiaLanding";
import { candidateMetaFor } from "@/lib/native/candidateLangs";
import { CANDIDATE_SLUGS, candidateCodeForBrowser, englishCandidatePath, nativeCandidatePath } from "@/lib/native/candidatePages";

const DISMISSED_KEY = "aiia:native-bar-dismissed";

// A one-line link on the English pages to the visitor's own-language page
// (/de, /fr, /es, /sv, /it), picked from the browser language. On the two
// English candidate pages it points to the same page in that language instead
// (/es/features/interview-warmup), for every language that has one. Never a
// redirect: Google asks for separate URLs and no automatic redirects. Fixed at
// the bottom left so it shifts no layout; the cookie banner sits bottom right.
// Shows after mount only, so the prerendered HTML never contains it.
export default function NativeLanguageBar({ pathname }) {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    if (NATIVE_BY_PATH[(pathname || "").replace(/\/+$/, "")]) return;
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(DISMISSED_KEY) === "1";
    } catch {
      dismissed = false;
    }
    if (dismissed) return;
    const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
    const page = (pathname || "").replace(/\/+$/, "");
    const slug = CANDIDATE_SLUGS.find((s) => englishCandidatePath(s) === page);
    if (slug) {
      const meta = candidateMetaFor(candidateCodeForBrowser(languages));
      setTarget(meta ? { ...meta, path: nativeCandidatePath(meta.code, slug), analyticsId: `native_bar_${meta.code}_${slug}` } : null);
      return;
    }
    setTarget(nativeLandingFor(languages));
  }, [pathname]);

  if (!target) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // Storage can be blocked; the bar then only closes for this page view.
    }
    setTarget(null);
  };

  return (
    <div
      lang={target.code}
      role="region"
      aria-label={target.nativeName}
      className="fixed bottom-4 left-4 z-40 flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-md border border-border bg-background px-4 py-2 text-sm shadow-md"
    >
      <a href={target.path} hrefLang={target.hreflang} data-analytics-id={target.analyticsId || `native_bar_${target.code}`} className="font-medium underline-offset-4 hover:underline">
        {target.banner} →
      </a>
      <button type="button" onClick={dismiss} aria-label="Close" className="text-muted-foreground hover:text-foreground">
        ×
      </button>
    </div>
  );
}
